/**
 * JournalYearlySummary — Phase 6c.1d / Bulgu BE.
 *
 * Single source of truth for the "Yıllık Konsolide Özet" panel that
 * surfaces a Big4-style debits/credits-by-account rollup of a journal
 * entry list. Previously the chrome (Card + chevron header + open/
 * close state) was inlined in the engagement-level `JournalEntriesTab`
 * — extracting it here lets the per-contract Yevmiye section render the
 * exact same panel without duplicating the toggle UI.
 *
 * The aggregation itself (`consolidateJEs`) is already a shared helper
 * in `journalEntryComponents.tsx`; this wrapper only owns the panel
 * chrome and the open/close state.
 *
 * Consumers:
 *   • `JournalEntriesTab` — engagement-level, default `testIdPrefix`
 *     keeps the existing `je-grand-total*` test ids stable.
 *   • Per-contract `JESection` in `ContractDetailPage.tsx` — passes a
 *     distinct `testIdPrefix` so the two panels can be queried
 *     independently when both are mounted in the same suite.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import {
  ConsolidatedSummaryTable,
  consolidateJEs,
  type TFn,
} from "@/pages/engagement/tabs/journalEntryComponents";
import type { AggregatedJournalEntry } from "@/types";

export interface JournalYearlySummaryProps {
  /**
   * Filtered journal entries to roll up. Caller is responsible for any
   * period / type / contract filtering — this component aggregates
   * whatever it's given.
   */
  entries: AggregatedJournalEntry[];
  currency: string;
  /**
   * Distinguishes the panel's test ids when more than one summary is
   * mounted (e.g. engagement-level + per-contract during navigation).
   * Defaults to `"je-grand-total"` so the engagement-level call site
   * keeps existing test ids.
   */
  testIdPrefix?: string;
  /**
   * Initial open/close state. Engagement-level wants `true`
   * (auditor-friendly forest view at the top). Per-contract is the
   * same — keep parity unless a caller has a reason to differ.
   */
  defaultOpen?: boolean;
}

export function JournalYearlySummary({
  entries,
  currency,
  testIdPrefix = "je-grand-total",
  defaultOpen = true,
}: JournalYearlySummaryProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  const summary = useMemo(
    () => consolidateJEs(entries, t as unknown as TFn),
    [entries, t],
  );

  if (entries.length === 0) return null;

  return (
    <Card className="p-0 overflow-hidden" data-testid={testIdPrefix}>
      <button
        type="button"
        className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdPrefix}-trigger`}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-base font-semibold">
          {t("je.grandTotal.title")}
        </span>
        <span className="text-sm text-muted-foreground">
          · {t("je.grandTotal.subtitle")}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid={`${testIdPrefix}-content`}>
          <ConsolidatedSummaryTable
            summary={summary}
            totalLabel={t("je.row.yearTotal")}
            currency={currency}
            testIdPrefix={testIdPrefix}
          />
        </div>
      )}
    </Card>
  );
}
